import React, { useEffect, useState, useRef } from "react";
import { collection, getDocs, doc, getDoc, updateDoc } from "firebase/firestore";
import { db } from "../config/firebase";
import PageTemplate from "../templates/PageTemplate";
import moment from "moment";
import { useNavigate } from "react-router-dom";

const STATUS_LABELS = {
  pending: "En attente",
  confirmed: "Confirmée",
  refused: "Refusée",
};

const STATUS_COLORS = {
  pending: "bg-yellow-100 text-yellow-800 border-yellow-200",
  confirmed: "bg-green-100 text-green-800 border-green-200",
  refused: "bg-red-100 text-red-800 border-red-200",
};

function InlineEdit({ value, onSave, disabled, type = "text", className = "" }) {
  const [editing, setEditing] = useState(false);
  const [text, setText] = useState(value ?? "");

  const handleSave = () => {
    setEditing(false);
    const newVal = type === "number" ? parseFloat(text) || 0 : text.trim();
    if (String(newVal) !== String(value ?? "")) {
      onSave(newVal);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter") handleSave();
    if (e.key === "Escape") { setText(value ?? ""); setEditing(false); }
  };

  if (editing) {
    return (
      <input
        autoFocus
        type={type}
        value={text}
        onChange={(e) => setText(e.target.value)}
        onBlur={handleSave}
        onKeyDown={handleKeyDown}
        disabled={disabled}
        className={`border border-gray-300 rounded px-2 py-1 text-sm focus:outline-none focus:ring-1 focus:ring-[#801111] focus:border-[#801111] ${className}`}
        onClick={(e) => e.stopPropagation()}
      />
    );
  }

  return (
    <span
      onClick={(e) => { e.stopPropagation(); setEditing(true); }}
      className="cursor-text hover:bg-gray-100 rounded px-1 py-0.5 -mx-1 transition-colors"
      title="Cliquer pour modifier"
    >
      {value != null && value !== "" ? (
        <span className={className}>{value}{type === "number" ? " €" : ""}</span>
      ) : (
        <span className="text-gray-400 italic">—</span>
      )}
    </span>
  );
}

export default function OffersList() {
  const [offers, setOffers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterStatus, setFilterStatus] = useState("all");
  const [updatingId, setUpdatingId] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    async function fetchOffers() {
      setLoading(true);
      const snap = await getDocs(collection(db, "offers"));
      const data = [];

      for (const docSnap of snap.docs) {
        const offer = docSnap.data();

        let authorEmail = "";
        if (offer.authorId && typeof offer.authorId === "string") {
          const userSnap = await getDoc(doc(db, "users", offer.authorId));
          if (userSnap.exists()) {
            authorEmail = userSnap.data().email || "";
          }
        }

        data.push({
          id: docSnap.id,
          message: offer.message,
          price: offer.price,
          projectId: offer.projectId,
          status: offer.status,
          authorEmail,
          createdAt: offer.createdAt,
        });
      }

      // Trier par date de création (plus récent en premier)
      data.sort((a, b) => {
        const dateA = a.createdAt?.toDate ? a.createdAt.toDate() : new Date(a.createdAt || 0);
        const dateB = b.createdAt?.toDate ? b.createdAt.toDate() : new Date(b.createdAt || 0);
        return dateB - dateA;
      });

      setOffers(data);
      setLoading(false);
    }
    fetchOffers();
  }, []);

  const showNotif = (msg) => {
    const div = document.createElement("div");
    div.className =
      "fixed top-4 right-4 bg-green-500 text-white px-6 py-3 rounded-lg shadow-lg z-50 text-sm";
    div.textContent = msg;
    document.body.appendChild(div);
    setTimeout(() => div.remove(), 3000);
  };

  const updateOfferField = async (id, field, value) => {
    setUpdatingId(id);
    try {
      await updateDoc(doc(db, "offers", id), { [field]: value });
      setOffers((prev) =>
        prev.map((o) => (o.id === id ? { ...o, [field]: value } : o))
      );
      showNotif("Offre mise à jour !");
    } catch {
      alert("Erreur lors de la mise à jour");
    } finally {
      setUpdatingId(null);
    }
  };

  const filteredOffers =
    filterStatus === "all"
      ? offers
      : offers.filter((o) => o.status === filterStatus);

  const statusCounts = {
    all: offers.length,
    pending: offers.filter((o) => o.status === "pending").length,
    confirmed: offers.filter((o) => o.status === "confirmed").length,
    refused: offers.filter((o) => o.status === "refused").length,
  };

  return (
    <PageTemplate
      loading={loading}
      content={
        <div className="space-y-4">
          {/* Filter tabs */}
          <div className="flex items-center gap-1 bg-white rounded-lg border border-gray-200 p-1">
            {[
              { key: "all", label: "Toutes" },
              { key: "pending", label: "En attente" },
              { key: "confirmed", label: "Confirmées" },
              { key: "refused", label: "Refusées" },
            ].map(({ key, label }) => (
              <button
                key={key}
                onClick={() => setFilterStatus(key)}
                className={`px-3 py-1.5 rounded-md text-xs font-medium transition-colors ${
                  filterStatus === key
                    ? "bg-gray-900 text-white"
                    : "text-gray-600 hover:bg-gray-100"
                }`}
              >
                {label} ({statusCounts[key]})
              </button>
            ))}
          </div>

          {/* Table */}
          <div className="bg-white rounded-lg border border-gray-200">
            {filteredOffers.length === 0 ? (
              <div className="text-center py-8">
                <p className="text-sm text-gray-500">Aucune offre trouvée</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="min-w-full text-sm">
                  <thead>
                    <tr className="border-b border-gray-200">
                      <th className="px-3 py-2.5 text-left text-xs font-medium text-gray-500 uppercase">Auteur</th>
                      <th className="px-3 py-2.5 text-left text-xs font-medium text-gray-500 uppercase">Message</th>
                      <th className="px-3 py-2.5 text-left text-xs font-medium text-gray-500 uppercase">Prix</th>
                      <th className="px-3 py-2.5 text-left text-xs font-medium text-gray-500 uppercase">Projet</th>
                      <th className="px-3 py-2.5 text-left text-xs font-medium text-gray-500 uppercase">Statut</th>
                      <th className="px-3 py-2.5 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {filteredOffers.map((o) => (
                      <tr key={o.id} onClick={() => navigate(`/offers/${o.id}`)} className="hover:bg-gray-50 cursor-pointer">
                        <td className="px-3 py-2.5 text-gray-900">{o.authorEmail || <span className="text-xs italic text-gray-400">Inconnu</span>}</td>
                        <td className="px-3 py-2.5 max-w-xs">
                          <InlineEdit
                            value={o.message}
                            onSave={(val) => updateOfferField(o.id, "message", val)}
                            disabled={updatingId === o.id}
                            className="text-xs text-gray-600"
                          />
                        </td>
                        <td className="px-3 py-2.5">
                          <InlineEdit
                            value={o.price}
                            onSave={(val) => updateOfferField(o.id, "price", val)}
                            disabled={updatingId === o.id}
                            type="number"
                            className="font-medium w-20"
                          />
                        </td>
                        <td className="px-3 py-2.5">
                          <span className="text-xs font-mono bg-gray-100 px-1.5 py-0.5 rounded">{o.projectId ? `${o.projectId.slice(0, 8)}...` : "-"}</span>
                        </td>
                        <td className="px-3 py-2.5" onClick={(e) => e.stopPropagation()}>
                          <select
                            disabled={updatingId === o.id}
                            value={o.status}
                            onChange={(e) => updateOfferField(o.id, "status", e.target.value)}
                            className="text-xs border border-gray-300 rounded px-2 py-1 focus:outline-none focus:ring-1 focus:ring-[#801111] focus:border-[#801111] disabled:opacity-50"
                          >
                            {Object.entries(STATUS_LABELS).map(([key, label]) => (
                              <option key={key} value={key}>{label}</option>
                            ))}
                          </select>
                        </td>
                        <td className="px-3 py-2.5 text-gray-500 whitespace-nowrap text-xs">
                          {o.createdAt && o.createdAt.toDate
                            ? moment(o.createdAt.toDate()).format("DD/MM/YYYY HH:mm")
                            : o.createdAt ? moment(o.createdAt).format("DD/MM/YYYY HH:mm") : "-"}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      }
    />
  );
}
